import {
	getSession,
} from 'Vulture/Session.js';

import {
	broncoURL,
} from 'Vulture/ENV.js';

const apiVersion = 'v1';
const apiURL = `${broncoURL}/${apiVersion}`;

const defaultHeaders = () => {
	const session = getSession();

	return {
		'Content-Type':        'application/json',
		'X-Auth-Session':      session.session,
		'X-Spark-API-version': window._getEnv('COMMITHASH'),
	};
};

const makeRequest = async (url, method, headers = {}, body, signal) => {
	headers = {
		...defaultHeaders(),
		...headers,
	};

	let accountUUID = window.location.pathname.split('/').pop();
	if (accountUUID && accountUUID.length === 36) {
		const urlObj = new URL(url);
		if (!urlObj.searchParams.get('account_uuid')) {
			urlObj.searchParams.append('account_uuid', accountUUID);
			url = urlObj.toString();
		}
	}

	try {
		let res = await fetch(url, {
			method,
			headers,
			body: body ? JSON.stringify(body) : undefined,
			...(signal && {
				signal,
			}),
		});

		if (!res.ok) {
			const errorDetails = await res.text();
			const err = new Error(`HTTP error: ${res.status} ${res.statusText} - ${errorDetails}`);

			// Callers need the payload, not just the message: /v1/batch reports which
			// action failed in `failed_index`, and handlers return {error} / {message}.
			err.status = res.status;
			try {
				err.body = JSON.parse(errorDetails);
			} catch (parseError) {
				err.body = null;
			}

			throw err;
		}

		console.log(res);

		try {
			let data = await res.json();

			return data;
		} catch (jsonError) {
			throw new Error(`Failed to parse JSON: ${jsonError.message}`);
		}
	} catch (err) {
		console.error(`Request to ${url} failed:`, err);

		// re-throw error so the promise gets rejected
		throw err;

		// return {
		// 	error: err.message,
		// };
	}
};

const makeURL = (path) => {
	let url = `${apiURL}/${path}`;

	// any route that starts with /v1 and is custom must be denoted as such or
	// the backend will not apply the proper handler during the routing phase
	if (path.startsWith('custom::')) {
		url = `${apiURL}${path.replace('custom::', '')}`.replace('/v1', '/');
	}

	return url;
};

const findRecords = async (m1, params = {}, signal) => {
	let url = makeURL(`${m1}s`);

	// Convert params to query string for GET requests
	if (params && Object.keys(params).length > 0) {
		if (params.includes && Array.isArray(params.includes)) {
			params.includes = params.includes.join(',');
		}

		const queryString = new URLSearchParams(params).toString();
		url += `?${queryString}`;
	}

	return makeRequest(url, 'GET', {}, null, signal);
};

const findRecord = async (m1, m1UUID, params = {}, signal) => {
	let url = makeURL(`${m1}s/${m1UUID}`);

	// Convert params to query string for GET requests
	if (params && Object.keys(params).length > 0) {
		if (params.includes && Array.isArray(params.includes)) {
			params.includes = params.includes.join(',');
		}

		const queryString = new URLSearchParams(params).toString();
		url += `?${queryString}`;
	}

	return makeRequest(url, 'GET', {}, null, signal);
};

const findMapRecords = async (m1, m1UUID, m2, signal) => {
	const url = makeURL(`${m1}s/${m1UUID}/${m2}s`);

	return makeRequest(url, 'GET', {}, null, signal);
};

const findMapRecord = async (m1, m1UUID, m2, m2UUID, signal) => {
	const url = makeURL(`${m1}s/${m1UUID}/${m2}s/${m2UUID}`);

	return makeRequest(url, 'GET', {}, null, signal);
};

const createRecord = async (m1, body, signal) => {
	const url = makeURL(`${m1}s`);

	return makeRequest(url, 'PUT', {}, body, signal);
};

const createMapRecord = async (m1, m1UUID, m2, body, signal) => {
	const url = makeURL(`${m1}s/${m1UUID}/${m2}s`);

	return makeRequest(url, 'PUT', {}, body, signal);
};

const updateRecord = async (m1, m1UUID, body, signal) => {
	const url = makeURL(`${m1}s/${m1UUID}`);

	return makeRequest(url, 'POST', {}, body, signal);
};

const updateMapRecord = async (m1, m1UUID, m2, m2UUID, body, signal) => {
	const url = makeURL(`${m1}s/${m1UUID}/${m2}s/${m2UUID}`);

	return makeRequest(url, 'POST', {}, body, signal);
};

const deleteRecord = async (m1, m1UUID, signal) => {
	const url = makeURL(`${m1}s/${m1UUID}`);

	return makeRequest(url, 'DELETE', {}, null, signal);
};

const deleteMapRecord = async (m1, m1UUID, m2, m2UUID, signal) => {
	const url = makeURL(`${m1}s/${m1UUID}/${m2}s/${m2UUID}`);

	return makeRequest(url, 'DELETE', {}, null, signal);
};

// VCL assignment reads/writes go through the custom
// /visualContentLoops/assignments endpoint (the POST also bumps the system's
// updated_at as a side effect). These thin helpers exist so callers don't
// inline the fetch/auth boilerplate.
const getVCLAssignments = async (accountUUID, signal) => {
	const url = `${broncoURL}/visualContentLoops/assignments/${accountUUID}`;

	return makeRequest(url, 'GET', {}, null, signal);
};

const updateVCLAssignment = async (accountUUID, componentID, visualContentLoopID, signal) => {
	const url = `${broncoURL}/visualContentLoops/assignments/${accountUUID}`;

	return makeRequest(url, 'POST', {}, {
		component_id:           componentID,
		visual_content_loop_id: visualContentLoopID,
	}, signal);
};

// Published Feeds (DEV-11955) — custom bronco routes; permission gating
// (external admin + account.allow_feed_publishing) happens server-side.
const publishFeed = async (slideDeckId, accountUUID, signal) => {
	const url = `${broncoURL}/slide_deck/${slideDeckId}/publish/${accountUUID}`;

	return makeRequest(url, 'POST', {}, {}, signal);
};

const unpublishFeed = async (slideDeckId, accountUUID, signal) => {
	const url = `${broncoURL}/slide_deck/${slideDeckId}/unpublish/${accountUUID}`;

	return makeRequest(url, 'POST', {}, {}, signal);
};

const getPublishedFeedState = async (slideDeckId, accountUUID, signal) => {
	const url = `${broncoURL}/slide_deck/${slideDeckId}/published_feed/${accountUUID}`;

	return makeRequest(url, 'GET', {}, null, signal);
};

const getAvailablePublishedFeeds = async (accountUUID, signal) => {
	const url = `${broncoURL}/published_feeds/available/${accountUUID}`;

	return makeRequest(url, 'GET', {}, null, signal);
};

const getPublishedFeedDeck = async (publishedFeedId, accountUUID, signal) => {
	const url = `${broncoURL}/published_feed/${publishedFeedId}/deck/${accountUUID}`;

	return makeRequest(url, 'GET', {}, null, signal);
};

const cloneTemplate = async (templateId, accountId, templateName, includeListingConfigs, signal) => {
	const url = `${broncoURL}/template/clone`;

	return makeRequest(url, 'POST', {}, {
		template_id:            templateId,
		account_id:             accountId,
		template_name:          templateName,
		include_listing_configs: includeListingConfigs || false,
	}, signal);
};

const saveTemplateSkeleton = async (payload, signal) => {
	const url = `${broncoURL}/template_skeleton/save`;

	return makeRequest(url, 'POST', {}, payload, signal);
};

// Several /v1 writes in one transaction (POST /v1/batch). Each action mirrors a
// single call: { op: 'create'|'update'|'delete'|'create_map'|'update_map'|'delete_map',
// m1, uuid?, m2?, m2_uuid?, body?, ref? }. A create may declare `ref`; later actions
// may use '$ref' wherever a uuid goes. All-or-nothing: the first failing action rolls
// everything back and the rejection body carries `failed_index`. Every action is
// permission- and scope-checked exactly like its single-record twin.
const batch = async (actions, accountUUID, signal) => {
	const url = makeURL('batch');
	const body = {
		actions,
	};

	if (accountUUID) {
		body.account_uuid = accountUUID;
	}

	return makeRequest(url, 'POST', {}, body, signal);
};

export default {
	findRecords,
	findRecord,
	findMapRecords,
	findMapRecord,
	createRecord,
	createMapRecord,
	updateRecord,
	updateMapRecord,
	deleteRecord,
	deleteMapRecord,
	batch,
	cloneTemplate,
	saveTemplateSkeleton,
	getVCLAssignments,
	updateVCLAssignment,
	publishFeed,
	unpublishFeed,
	getPublishedFeedState,
	getAvailablePublishedFeeds,
	getPublishedFeedDeck,
};